/* global $ */
import React, { useState } from 'react';
import axios from 'axios';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { saveAs } from 'file-saver';
import * as XLSX from 'sheetjs-style';

const ConsolidatedReport = () => {
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [reportData, setReportData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const handleFetchReport = async () => {
    if (!fromDate || !toDate) {
      alert('Both dates are required.');
      return;
    }

    setIsLoading(true);
    try {
      const response = await axios.get(`https://api.eazyworldehsengineering.com/api/candidate_registration/report/consolidated_report`, {
        params: {
          from_date: fromDate,
          to_date: toDate,
        },
      });

      const reportData = response.data.data;
      handleDownloadExcel(reportData);

    } catch (error) {
      console.error('Error fetching report data:', error);
      alert('Error fetching report data. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDownloadPDF = () => {
    const doc = new jsPDF('l');
    doc.setFontSize(14);
    doc.text('Consolidated Report', 40, 30);
    doc.setFontSize(10);
    doc.text(`From: ${fromDate} To: ${toDate}`, 40, 45);

    autoTable(doc, {
      startY: 60,
      head: [[
        'S No', 'Batch No', 'Training Title', 'Training Date', 'Payment Status',
        'Nos', 'Name of Participant', 'Organization', 'Evaluation Status',
        'Pass/Fail', 'Certificate Status'
      ]],
      body: reportData.map((row, idx) => [
        idx + 1,
        row.batch_no,
        row.training_title,
        new Date(row.training_date).toLocaleDateString(),
        row.amount_status,
        row.participant_number,
        row.Name,
        row.organization,
        row.evaluation_option,
        row.evaluate_grade_status,
        row.reg_form === 'Fresh' ? 'Fresh' : 'Renewal'
      ]),
      styles: { fontSize: 7, cellPadding: 2 },
      headStyles: { fillColor: [51, 204, 0] },
    });

    doc.save('ConsolidatedReport.pdf');
  };

  const handleDownloadExcel = (reportData) => {
    const fileName = `Consolidated_Report_From_${fromDate}_To_${toDate}.xlsx`;

    const headers = [
      "Batch No", "Training Title", "Training Date", "Certificate Status",
      "Nos", "Name of Participant", "Organization", "Evaluation Status",
      "Pass/Fail"
    ];

    const data = reportData.map((row, index) => [
      row.batch_no || "",
      row.training_title || "",
      row.training_date ? new Date(row.training_date).toLocaleDateString('en-GB') : "",
      row.reg_form === 'Fresh' ? 'Fresh' : 'Renewal',
      row.participant_number || "",
      row.Name || "",
      row.organization || "",
      row.evaluation_option || "",
      row.evaluate_grade_status || ""
    ]);

    // Create worksheet data with proper structure
    const worksheetData = [
      ["Consolidated Report"], // Title row
      [`From: ${fromDate.split('-').reverse().join('-')} To: ${toDate.split('-').reverse().join('-')}`], // Date row
      [], // Empty row
      headers, // Header row
      ...data // Data rows
    ];

    const ws = XLSX.utils.aoa_to_sheet(worksheetData);

    // Set proper merge ranges
    ws["!merges"] = [
      { s: { r: 0, c: 0 }, e: { r: 0, c: headers.length - 1 } }, // Title row merge
      { s: { r: 1, c: 0 }, e: { r: 1, c: headers.length - 1 } }, // Date row merge
    ];

    // Get the range of the worksheet
    const range = XLSX.utils.decode_range(ws["!ref"]);

    // Apply styles to all cells
    for (let R = range.s.r; R <= range.e.r; ++R) {
      for (let C = range.s.c; C <= range.e.c; ++C) {
        const cellAddress = XLSX.utils.encode_cell({ r: R, c: C });
        const cell = ws[cellAddress];
        if (!cell) continue;

        // Default style for all cells
        let style = {
          font: { sz: 11, color: { rgb: "000000" } },
          alignment: { vertical: "center", horizontal: "center", wrapText: true },
          border: {
            top: { style: "thin", color: { rgb: "000000" } },
            bottom: { style: "thin", color: { rgb: "000000" } },
            left: { style: "thin", color: { rgb: "000000" } },
            right: { style: "thin", color: { rgb: "000000" } },
          },
        };

        // Title row (Row 0): "Consolidated Report"
        if (R === 0) {
          style.font = { bold: true, sz: 18, color: { rgb: "000000" } };
          style.alignment = { horizontal: "center", vertical: "center" };
          style.border = {
            top: { style: "thin", color: { rgb: "000000" } },
            bottom: { style: "thin", color: { rgb: "000000" } },
            left: { style: "thin", color: { rgb: "000000" } },
            right: { style: "thin", color: { rgb: "000000" } },
          };
        }
        // Date row (Row 1): "From: ... To: ..."
        else if (R === 1) {
          style.font = { bold: false, sz: 13, color: { rgb: "000000" } };
          style.alignment = { horizontal: "center", vertical: "center" };
          style.border = {
            top: { style: "thin", color: { rgb: "000000" } },
            bottom: { style: "thin", color: { rgb: "000000" } },
            left: { style: "thin", color: { rgb: "000000" } },
            right: { style: "thin", color: { rgb: "000000" } },
          };
        }
        // Empty row (Row 2)
        else if (R === 2) {
          style.border = {
            top: { style: "thin", color: { rgb: "000000" } },
            bottom: { style: "thin", color: { rgb: "000000" } },
            left: { style: "thin", color: { rgb: "000000" } },
            right: { style: "thin", color: { rgb: "000000" } },
          };
        }
        // Header row (Row 3): Column headers with bright green background
        else if (R === 3) {
          style.font = { bold: true, color: { rgb: "000000" }, sz: 10 }; // Black text, smaller size
          style.fill = { fgColor: { rgb: "33CC00" } }; // Bright green background
          style.alignment = { horizontal: "center", vertical: "center", wrapText: true };
          style.border = {
            top: { style: "thin", color: { rgb: "000000" } },
            bottom: { style: "thin", color: { rgb: "000000" } },
            left: { style: "thin", color: { rgb: "000000" } },
            right: { style: "thin", color: { rgb: "000000" } },
          };
        }
        // Data rows (Row 4 onwards)
        else {
          // Different alignment for specific columns
          if (C === 0 || C === 1 || C === 3 || C === 4 || C === 5) { // S No, Batch No, Training Date, Payment Status, Nos - center
            style.alignment = { horizontal: "center", vertical: "center" };
          } else if (C === 6 || C === 8 || C === 9 || C === 10) { // Name, Evaluation Status, Pass/Fail, Certificate Status - center
            style.alignment = { horizontal: "center", vertical: "center" };
          } else { // Other columns - left aligned
            style.alignment = { horizontal: "left", vertical: "center" };
          }

          style.font = { sz: 10, color: { rgb: "000000" } }; // Smaller font for data
          style.border = {
            top: { style: "thin", color: { rgb: "000000" } },
            bottom: { style: "thin", color: { rgb: "000000" } },
            left: { style: "thin", color: { rgb: "000000" } },
            right: { style: "thin", color: { rgb: "000000" } },
          };
        }

        cell.s = style;
      }
    }

    // Set column widths to show full data values
    ws["!cols"] = [      
      { wch: 10 },  // Batch No
      { wch: 35 },  // Training Title - increased for full topic names
      { wch: 12 },  // Training Date
      { wch: 15 },  // Payment Status
      { wch: 6 },   // Nos
      { wch: 20 },  // Name of Participant
      { wch: 30 },  // Organization - increased to show full name
      { wch: 20 },  // Evaluation Status
      { wch: 10 },  // Pass/Fail
    ];

    // Set row heights
    ws["!rows"] = [
      { hpt: 25 }, // Title row
      { hpt: 20 }, // Date row
      { hpt: 10 }, // Empty row
      { hpt: 25 }, // Header row
      // Data rows will use default height
    ];

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Consolidated Report");

    const excelBuffer = XLSX.write(wb, {
      bookType: "xlsx",
      type: "array",
      cellStyles: true,
    });

    const dataBlob = new Blob([excelBuffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8",
    });

    saveAs(dataBlob, fileName);
  };

  return (
    <div className="content-wrapper">
      <section className="content-header">
        <div className="container-fluid">
          <h1>Consolidated Report</h1>
        </div>
      </section>

      <section className="content">
        <div className="container-fluid">
          <div className="card card-primary card-outline">
            <div className="card-body">
              <div className="row">
                <div className="col-md-4">
                  <label>From Date</label>
                  <input
                    type="date"
                    className="form-control"
                    value={fromDate}
                    onChange={e => setFromDate(e.target.value)}
                  />
                </div>

                <div className="col-md-4">
                  <label>To Date</label>
                  <input
                    type="date"
                    className="form-control"
                    value={toDate}
                    onChange={e => setToDate(e.target.value)}
                  />
                </div>

                <div className="col-md-4 d-flex align-items-end">
                  <button className="btn btn-primary w-100" onClick={handleFetchReport}>
                    View Report
                  </button>
                </div>
              </div>

              {isLoading && <p className="mt-3 text-muted">Loading report...</p>}

              {reportData.length > 0 && (
                <>
                  <div className="mt-4 text-right">
                    <button className="btn btn-sm btn-danger mr-2" onClick={handleDownloadPDF}>Download PDF</button>
                    <button className="btn btn-sm btn-success" onClick={handleDownloadExcel}>Download Excel</button>
                  </div>

                  <div className="table-responsive mt-3">
                    <table className="table table-bordered table-sm table-striped">
                      <thead className="bg-secondary text-white">
                        <tr>
                          <th>Batch No</th>
                          <th>Training Title</th>
                          <th>Training Date</th>
                          <th>Certificate Status</th>
                          <th>Nos</th>
                          <th>Name of Participant</th>
                          <th>Organization</th>
                          <th>Evaluation Status</th>
                          <th>Pass/Fail</th>
                        </tr>
                      </thead>
                      <tbody>
                        {reportData.map((row, idx) => (
                          <tr key={idx}>
                            <td>{row.batch_no}</td>
                            <td>{row.training_title}</td>
                            <td>{new Date(row.training_date).toLocaleDateString()}</td>
                            <td>{row.reg_form === 'Fresh' ? 'Fresh' : 'Renewal'}</td>
                            <td>{row.participant_number}</td>
                            <td>{row.Name}</td>
                            <td>{row.organization}</td>
                            <td>{row.evaluation_option}</td>
                            <td>{row.evaluate_grade_status}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ConsolidatedReport;